// "Nuevos ángulos" (carrusel) — webhook n8n `generar_nuevos_angulos` migrado a
// atlas `/v1/generate/angles` (ADR-600).
// Flag ADENTRO del helper:
//   ON  → atlas con JWT (`user_id` NO viaja) + `modo_utilizado:
//         'nuevos_angulos'`. Respuesta SIEMPRE objeto
//         `{response:'success', imagenes_generadas_ids}`. Sin créditos = 402
//         tipado (n8n se colgaba) → GenerateAdError billing (isActionable:
//         los callers muestran el copy de saldo en su alert).
//   OFF → webhook n8n byte-idéntico (que a veces respondía ARRAY — el parse
//         tolerante vive acá, los callers reciben los ids listos).
// La espera puede ser de 2-3 min, igual que hoy.
//
// DOS FUENTES posibles (ADR-2026-08-14-modo-carrusel): una imagen que ya generó
// Atlas (`image_id`) o una FOTO SUBIDA por el usuario (`image_bucket`+`image_path`,
// referencia de storage). La subida sólo la entiende el backend nuevo — con el flag
// apagado el modo Carrusel esconde el botón de subir, igual que hace Animar.

import { GENERATE_AUTH_TOKEN, USE_ATLAS_BACKEND } from '../config';
import { atlasBackendFetch } from './atlasBackend';
import { GenerateAdError } from './generateAdRequest';
import type { ResolucionImagen } from './resolucionImagen';

const GENERAR_NUEVOS_ANGULOS_URL =
  'https://ai-agency-dt7d.onrender.com/webhook/generar_nuevos_angulos';

// Mismo copy de saldo que las generaciones de imagen (generateAdRequest).
const BILLING_MESSAGE =
  'No te alcanzan los créditos para esta generación. Sumá créditos o actualizá tu plan para continuar.';

/** De dónde sale la imagen de la que parte el carrusel. Espeja `AnimarSource`. */
export type AnglesSource =
  | { tipo: 'generada'; imageId: string | number }
  | { tipo: 'subida'; bucket: string; path: string };

/** Con el flag apagado se va a n8n, que no entiende imágenes sueltas: el modo
 *  esconde "Subir imagen" y solo deja elegir una generada. */
export const ANGLES_SUBIDA_REQUIERE_ATLAS = !USE_ATLAS_BACKEND;

/** Campos de fuente que entiende el backend, según de dónde salga la imagen. */
function sourceFields(source: AnglesSource): Record<string, unknown> {
  return source.tipo === 'generada'
    ? { image_id: String(source.imageId) }
    : { image_bucket: source.bucket, image_path: source.path };
}

/** Devuelve los ids de la tanda (strings). Vacío = falló (alert genérico del caller). */
export async function postGenerateAngles(body: {
  user_id: string;
  image_id: string | number;
  cantidad_imagenes?: number;
  /** Resolución de cada ángulo. Sólo la manda la producción de las plantillas
   *  (ADR-2026-09-28-las-plantillas-eligen-la-calidad); sin ella, 1K como siempre. */
  image_size?: ResolucionImagen;
}): Promise<string[]> {
  return postGenerateAnglesFrom(
    { tipo: 'generada', imageId: body.image_id },
    { userId: body.user_id, cantidad: body.cantidad_imagenes, imageSize: body.image_size },
  );
}

/** Igual que `postGenerateAngles` pero para cualquiera de las dos fuentes. */
export async function postGenerateAnglesFrom(
  source: AnglesSource,
  opts: {
    /** Solo para el fallback n8n (el backend nuevo usa el JWT). */
    userId: string;
    cantidad?: number;
    /** Sólo el backend nuevo la entiende: n8n genera siempre en 1K. */
    imageSize?: ResolucionImagen;
  },
): Promise<string[]> {
  if (USE_ATLAS_BACKEND) {
    const res = await atlasBackendFetch('/v1/generate/angles', {
      ...sourceFields(source),
      ...(opts.cantidad !== undefined ? { cantidad_imagenes: opts.cantidad } : {}),
      ...(opts.imageSize ? { image_size: opts.imageSize } : {}),
      modo_utilizado: 'nuevos_angulos',
    });
    if (res.status === 402) {
      throw new GenerateAdError('billing', BILLING_MESSAGE, 'billing');
    }
    const data = (await res.json().catch(() => null)) as {
      imagenes_generadas_ids?: Array<string | number>;
    } | null;
    const ids = data?.imagenes_generadas_ids;
    return Array.isArray(ids) ? ids.map(String) : [];
  }

  if (source.tipo === 'subida') {
    throw new Error('Las imágenes subidas necesitan el backend nuevo.');
  }
  const res = await fetch(GENERAR_NUEVOS_ANGULOS_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: GENERATE_AUTH_TOKEN,
    },
    body: JSON.stringify({
      user_id: opts.userId,
      image_id: source.imageId,
      ...(opts.cantidad !== undefined ? { cantidad_imagenes: opts.cantidad } : {}),
    }),
  });
  let data: any = null;
  try {
    data = await res.json();
  } catch {
    return [];
  }
  if (Array.isArray(data) && data[0]?.imagenes_generadas_ids) {
    return (data[0].imagenes_generadas_ids as Array<string | number>).map(String);
  }
  if (data?.imagenes_generadas_ids) {
    return (data.imagenes_generadas_ids as Array<string | number>).map(String);
  }
  return [];
}
